const STOREFRONT_DISABLED_RE =
  /product storefront is not enabled/i;

export function isStorefrontDisabledError(message?: string | null) {
  return STOREFRONT_DISABLED_RE.test(message || "");
}

export function payloadHasStorefrontDisabledError(data: unknown) {
  if (!data || typeof data !== "object") return false;
  return Object.values(data as Record<string, unknown>).some((payload) => {
    if (!payload || typeof payload !== "object") return false;
    const record = payload as {
      status?: number;
      errors?: Array<{ message?: string | null } | null> | null;
    };
    return (
      record.status != null &&
      record.status !== 200 &&
      isStorefrontDisabledError(record.errors?.[0]?.message)
    );
  });
}

export function checkoutErrorMessage(
  raw?: string | null,
  fallback = "Unable to start product checkout. Please try again.",
) {
  if (isStorefrontDisabledError(raw)) {
    return "Unable to start product checkout. Please refresh and try again.";
  }
  const trimmed = raw?.trim();
  return trimmed || fallback;
}

export const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export async function retryOnStorefrontDisabled<T>(
  run: () => Promise<T>,
  options: {
    getMessage: (result: T) => string | null | undefined;
    isSuccess: (result: T) => boolean;
    attempts?: number;
  },
): Promise<T> {
  const attempts = options.attempts ?? 3;
  let lastResult: T | undefined;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const result = await run();
      lastResult = result;
      if (options.isSuccess(result)) return result;
      const message = options.getMessage(result);
      if (!isStorefrontDisabledError(message) || attempt === attempts - 1) {
        return result;
      }
    } catch (error) {
      lastError = error;
      const message = error instanceof Error ? error.message : "";
      if (!isStorefrontDisabledError(message) || attempt === attempts - 1) {
        throw error;
      }
    }
    await wait(350 * (attempt + 1));
  }

  if (lastResult !== undefined) return lastResult;
  throw lastError;
}
